/**
 * iter_193 — Public Membership Badges (Batch A).
 *
 * Reads a plain user-like object (from ANY public endpoint — /api/artist/{id},
 * comment author, contest entry author, live participant, etc.) and renders
 * the small set of PUBLIC membership badges: Premium, Premium Yearly,
 * Founding Artist, Admin.
 *
 * DOES NOT consume /api/me/tier — that is the viewer's OWN tier, never
 * another user's. Data comes from public fields:
 *   • is_admin            → Admin
 *   • role === "admin"    → Admin (fallback)
 *   • founder_number OR
 *     badges.includes(founder_artist|founding_100)
 *                         → Founding Artist
 *   • membership_tier === "premium_monthly"     → Premium
 *   • membership_tier === "premium_yearly"      → Premium (Yearly)
 *
 * Billing dates, Stripe IDs, invoice info — NEVER rendered here.
 */
const BADGE_DEFS = {
  admin: {
    label: "Admin",
    icon: "⚡",
    bg: "from-purple-600 to-purple-700",
    text: "text-white",
    title: "RGM Studio Admin",
  },
  founding_artist: {
    label: "Founding Artist",
    icon: "⭐",
    bg: "from-amber-400 via-pink-500 to-purple-600",
    text: "text-white",
    title: "Founding 100 Artist",
  },
  premium_yearly: {
    label: "Premium · Yearly",
    icon: "💎",
    bg: "from-cyan-400 to-blue-500",
    text: "text-white",
    title: "RGM Premium (Yearly)",
  },
  premium: {
    label: "Premium",
    icon: "💎",
    bg: "from-cyan-400 to-blue-500",
    text: "text-white",
    title: "RGM Premium",
  },
};

const SIZE = {
  sm: "text-[10px] px-2 py-0.5 gap-1",
  md: "text-xs px-2.5 py-1 gap-1.5",
  lg: "text-sm px-3 py-1.5 gap-2",
};

/**
 * Given a public user-like object, compute the ordered list of badges
 * that should render. Order = admin → founding_artist → premium.
 */
export const resolveMembershipBadges = (user = {}) => {
  const out = [];
  if (!user || typeof user !== "object") return out;
  const badges = Array.isArray(user.badges) ? user.badges : [];
  const isAdmin = user.is_admin === true || user.role === "admin";
  if (isAdmin) out.push("admin");
  const isFounder =
    !!user.founder_number ||
    badges.includes("founder_artist") ||
    badges.includes("founding_artist") ||
    badges.includes("founding_100");
  if (isFounder) out.push("founding_artist");
  const tier = user.membership_tier;
  if (tier === "premium_yearly") out.push("premium_yearly");
  else if (tier === "premium_monthly") out.push("premium");
  return out;
};

const MembershipBadge = ({ type, size = "sm", testIdPrefix = "membership-badge" }) => {
  const def = BADGE_DEFS[type];
  if (!def) return null;
  return (
    <span
      title={def.title}
      data-testid={`${testIdPrefix}-${type}`}
      className={`inline-flex items-center rounded-full font-bold bg-gradient-to-r ${def.bg} ${def.text} ${SIZE[size] || SIZE.sm} whitespace-nowrap`}
    >
      <span aria-hidden>{def.icon}</span>
      <span>{def.label}</span>
    </span>
  );
};

/**
 * MembershipBadges — drop-in strip of badges.
 * Renders NOTHING when the user has no public tier / founder / admin data.
 *
 * Props:
 *   • user        — public user-like object
 *   • size        — 'sm' | 'md' | 'lg'
 *   • testIdPrefix— optional test id prefix (default 'membership-badge')
 *   • className   — wrapper class
 */
const MembershipBadges = ({ user, size = "sm", testIdPrefix = "membership-badge", className = "" }) => {
  const types = resolveMembershipBadges(user);
  if (types.length === 0) return null;
  return (
    <span
      className={`inline-flex items-center flex-wrap gap-1.5 ${className}`}
      data-testid={`${testIdPrefix}-strip`}
    >
      {types.map((t) => (
        <MembershipBadge key={t} type={t} size={size} testIdPrefix={testIdPrefix} />
      ))}
    </span>
  );
};

export default MembershipBadges;
